//Dependencies............................................
import React, { useEffect, useState } from "react";
import axios from 'axios';
import {Input, Modal, Form} from 'antd';
import {
    useQueryClient,
    useMutation,
} from '@tanstack/react-query'

//Components..............................................
import {BASE_URL} from '../../../utils/constants';
import {JsonSchemaItem} from "../../../utils/interfaces";
import { NotificationInstance } from "antd/es/notification/interface";

interface Props {
  jsonSchema: JsonSchemaItem,
  showModal: boolean,
  onClose: () => void
  api: NotificationInstance
}



const EditJsonSchemaListModal: React.FC<Props> = ({ jsonSchema, showModal, onClose: onCancel, api }: Props) => {

  const [form] = Form.useForm<JsonSchemaItem>();
  const [submittable, setSubmittable] = useState(false);
  const [initialJsonSchemaString, setInitialJsonSchemaString] = useState<string | undefined>(undefined);

  const watchedValues = Form.useWatch([], form);
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, watchedValues]);

  const isDirty = watchedValues?.json_schema !== undefined
    && initialJsonSchemaString !== undefined
    && watchedValues.json_schema !== initialJsonSchemaString;

  const queryClient = useQueryClient()

  const editJsonSchema = useMutation({
    // mutationFn: (values: JsonSchemaItem) => axios.post(`${BASE_URL}update_json_schema_base/${jsonSchema.id}`, values),
    mutationFn: (values: JsonSchemaItem) => {
      return axios.post(`${BASE_URL}/update_json_schema_base/${jsonSchema.id}`, values, {
        headers: {
        // Overwrite Axios's automatically set Content-Type
        'Content-Type': 'application/json'
      }})
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jsonSchemaList'] })
      queryClient.invalidateQueries({ queryKey: ['baseFirmwareList'] })
      queryClient.invalidateQueries({ queryKey: ['vehicleList'] })
    }
  })

  const onFinish = (values: JsonSchemaItem) => {
    console.log('Received values of form: ', values);
    values.json_schema = JSON.parse(values.json_schema as string)
    editJsonSchema.mutate(values, {
      onSuccess: () =>  {
        onCancel()
        api.success({
          title: 'JSON Schema Updated',
          description: `JSON Schema updated successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
      },
      onError: (error) => {
        api.error({
          title: 'Error Editing JSON Schema',
          description: `Failed to edit JSON Schema: ${error.message}`,
          placement: 'bottomRight',
          showProgress: true
        });
      }
    });
  };

  useEffect(() => {
    if (showModal && jsonSchema) {
      setTimeout(() => {
        const jsonSchemaString = JSON.stringify(jsonSchema?.json_schema, undefined, 4);
        form.setFieldsValue({
          json_schema: jsonSchemaString,
        });
        setInitialJsonSchemaString(jsonSchemaString);
      }, 0);
    }
  }, [showModal, jsonSchema, form]);

  return(
    <Modal
      title="Edit JSON Schema"
      open={showModal}
      okText="Update"
      cancelText="Cancel"
      okButtonProps={{
        autoFocus: false,
        htmlType: 'submit',
        loading: editJsonSchema.isPending,
        disabled: !submittable || !isDirty || editJsonSchema.isPending,
      }}
      cancelButtonProps={{ disabled: editJsonSchema.isPending }}
      closable={!editJsonSchema.isPending}
      mask={{ closable: !editJsonSchema.isPending }}
      onCancel={onCancel}
      modalRender={(dom) => (
        <Form
          layout="vertical"
          form={form}
          name="form_edit_json_schema"
          clearOnDestroy
          onFinish={(values) => onFinish(values)}
        >
          {dom}
        </Form>
      )}
    >
      <Form.Item
        name="json_schema"
        label="JSON Schema"
        rules={[
          {
            required: true,
            validator: (_, value: string) => {
              if(value) {
                try {
                  JSON.parse(value);
                  return Promise.resolve()
                } catch (e) {
                  return Promise.reject(new Error('Invalid JSON'))
                }
              } else {
                return Promise.reject(new Error('Please enter JSON Schema'))
              }
            }
          },
        ]}>
        <Input.TextArea rows={30} />
      </Form.Item>
    </Modal>
  )
}
export default EditJsonSchemaListModal;

